/**
 * Classe RT (scontrino / corrispettivi) DERIVATA dalla faccia SdI.
 *
 * Il catalogo IVA (`pos_vat_rates`) e i listini esprimono UNA sola faccia
 * scrivibile: quella SdI (aliquota, natura con sotto-codice, riferimento
 * normativo). La classe RT non si sceglie né si salva a catalogo/listino:
 * si CALCOLA con questa unica funzione di proiezione.
 *
 *   - riga imponibile            → classe = aliquota (22, 10, 5, 4, …)
 *   - natura N1…N6 e sotto-codici → classe = radice intera (N2.1 → N2, N6.3 → N6)
 *   - N7                          → rifiutata (non prevista dai corrispettivi)
 *   - codice sconosciuto          → rifiutato
 *
 * È l'UNICA fonte della regola: consumata dal mapping stampante (classe →
 * reparto), dallo snapshot fiscale della riga di vendita, dal gate reparti e
 * dal badge read-only nei listini. Parità 1:1 con la funzione DB
 * `public.pos_vat_rt_reparto_allowed` (vedi migration dedicata).
 */

import { isPosVatNatureCode } from './natura-iva-map';

/** Radici natura ammesse sul tracciato corrispettivi v7.0. */
export const RT_NATURE_ROOTS = ['N1', 'N2', 'N3', 'N4', 'N5', 'N6'] as const;
export type RtNatureRoot = (typeof RT_NATURE_ROOTS)[number];

/** Classe RT: aliquota imponibile oppure radice natura intera. */
export type RtClass =
  | { readonly kind: 'rate'; readonly rate: number }
  | { readonly kind: 'nature'; readonly root: RtNatureRoot };

/**
 * Chiave testuale canonica della classe (persistita su `pos_sale_details.rt_class`
 * e nel mapping stampante): `"22"`, `"10"`, `"4"`, `"N2"`, … Le aliquote sono
 * normalizzate senza zeri/decimali superflui (`22.00` → `"22"`, `4.5` → `"4.5"`).
 */
export type RtClassKey = `${number}` | RtNatureRoot;

export type RtClassProjection =
  | { readonly ok: true; readonly rtClass: RtClass; readonly key: RtClassKey }
  | { readonly ok: false; readonly motivo: string };

export const RT_CLASS_MOTIVO_N7 =
  'IVA assolta in altro Stato UE (N7): natura non prevista dai corrispettivi, nessuna classe scontrino';
export const RT_CLASS_MOTIVO_NATURA_SCONOSCIUTA =
  'codice natura IVA non riconosciuto: nessuna classe scontrino';
export const RT_CLASS_MOTIVO_ALIQUOTA_INVALIDA =
  'aliquota non numerica o negativa: nessuna classe scontrino';

export function isRtNatureRoot(v: string): v is RtNatureRoot {
  return (RT_NATURE_ROOTS as readonly string[]).includes(v);
}

/** Radice intera di un codice natura (`N2.1` → `N2`); `null` se non proiettabile (N7, sconosciuto). */
export function rtNatureRootOf(nature: string): RtNatureRoot | null {
  if (!isPosVatNatureCode(nature)) return null;
  const root = nature.split('.')[0] ?? nature;
  return isRtNatureRoot(root) ? root : null;
}

function normalizeRateKey(rate: number): `${number}` {
  // Number→string già senza zeri superflui; +() elimina "-0".
  return `${+rate.toFixed(2)}`;
}

export function rtClassKey(rtClass: RtClass): RtClassKey {
  return rtClass.kind === 'rate' ? normalizeRateKey(rtClass.rate) : rtClass.root;
}

/**
 * UNICA funzione di proiezione: riga `pos_vat_rates` (o snapshot equivalente)
 * → classe RT. Rifiuta con motivo parlante N7 e codici sconosciuti.
 */
export function projectRtClass(row: {
  readonly rate: number | string | null | undefined;
  readonly is_taxable?: boolean | null;
  readonly nature_code?: string | null;
}): RtClassProjection {
  const nature = row.nature_code ?? null;
  const taxable = row.is_taxable === true || nature === null || nature === '';
  if (taxable) {
    const rate = row.rate == null ? NaN : Number(row.rate);
    if (!Number.isFinite(rate) || rate < 0) return { ok: false, motivo: RT_CLASS_MOTIVO_ALIQUOTA_INVALIDA };
    const rtClass: RtClass = { kind: 'rate', rate: +rate.toFixed(2) };
    return { ok: true, rtClass, key: rtClassKey(rtClass) };
  }
  if (!isPosVatNatureCode(nature)) return { ok: false, motivo: RT_CLASS_MOTIVO_NATURA_SCONOSCIUTA };
  if (nature === 'N7') return { ok: false, motivo: RT_CLASS_MOTIVO_N7 };
  const root = rtNatureRootOf(nature);
  if (!root) return { ok: false, motivo: RT_CLASS_MOTIVO_NATURA_SCONOSCIUTA };
  const rtClass: RtClass = { kind: 'nature', root };
  return { ok: true, rtClass, key: rtClassKey(rtClass) };
}

/** Riconosce una chiave classe RT persistita (`"22"`, `"4.5"`, `"N6"`). */
export function isRtClassKey(v: unknown): v is RtClassKey {
  if (typeof v !== 'string' || v.length === 0) return false;
  if (isRtNatureRoot(v)) return true;
  if (!/^\d+(\.\d{1,2})?$/.test(v)) return false;
  return normalizeRateKey(Number(v)) === v;
}

/** Parse di una chiave persistita in `RtClass`; `null` se non valida. */
export function parseRtClassKey(v: unknown): RtClass | null {
  if (!isRtClassKey(v)) return null;
  return isRtNatureRoot(v) ? { kind: 'nature', root: v } : { kind: 'rate', rate: Number(v) };
}

/** Etichetta operatore: `"22%"`, `"N2"`. */
export function rtClassLabel(key: RtClassKey): string {
  return isRtNatureRoot(key) ? key : `${key}%`;
}

/** Ordinamento stabile: aliquote decrescenti, poi nature N1..N6. */
export function compareRtClassKeys(a: RtClassKey, b: RtClassKey): number {
  const an = isRtNatureRoot(a);
  const bn = isRtNatureRoot(b);
  if (an !== bn) return an ? 1 : -1;
  if (an && bn) return a.localeCompare(b);
  return Number(b) - Number(a);
}
